import type { UUID } from '@aline/common';
import { EditingMode, PermissionRole } from '@aline/common';
import type { Apollo } from 'src/gql/generated';
import { getEntityDisplayDetails } from 'src/helpers/accounts';
import { appColors, roleAvatarColor } from 'src/theme';
import type { AccountSubset } from 'src/types';

export function userComparator(
  userA: Pick<Apollo.User, 'firstName' | 'email'>,
  userB: Pick<Apollo.User, 'firstName' | 'email'>
): number {
  const compareStringA = userA.firstName ?? userA.email;
  const compareStringB = userB.firstName ?? userB.email;

  return compareStringA.localeCompare(compareStringB);
}

export function compareByEmail(
  userA: Pick<Apollo.User, 'email' | '__typename'>,
  userB: Pick<Apollo.User, 'email' | '__typename'>
): number {
  return userA.email.localeCompare(userB.email);
}

export function userWithPlural(userIds: UUID[]): string {
  return userIds.length <= 1 ? 'user' : 'users';
}

export function mostPermissiveEditingMode(userRole: PermissionRole): EditingMode {
  switch (userRole) {
    case PermissionRole.Owner:
    case PermissionRole.Editor:
      return EditingMode.EDITING;
    case PermissionRole.Suggester:
      return EditingMode.SUGGESTING;
    case PermissionRole.Commenter:
      return EditingMode.COMMENTING;
    case PermissionRole.Viewer:
      return EditingMode.VIEWING;
  }
}

export const permissionsAscOrder: PermissionRole[] = [
  PermissionRole.Viewer,
  PermissionRole.Commenter,
  PermissionRole.Suggester,
  PermissionRole.Editor,
  PermissionRole.Owner,
];

interface DisplayDetails {
  primaryText: string;
  avatarColor?: string;
  secondaryText?: string;
}

export function getRoleDisplayDetails(
  role: Pick<Apollo.UserDefinedRole, 'roleName'>
): DisplayDetails {
  return { avatarColor: roleAvatarColor, primaryText: role.roleName };
}

export function getAssigneeDisplayDetails(
  assignee?: AccountSubset | null,
  assigneeRole?: Partial<Pick<Apollo.UserDefinedRole, 'roleName'>> | null
): ReturnType<typeof getEntityDisplayDetails> {
  const assigneeDetails = assignee ? getEntityDisplayDetails(assignee) : null;
  const assigneeRoleDetails = assigneeRole?.roleName
    ? { avatarColor: appColors.iconsType, primary: assigneeRole.roleName }
    : null;

  return assigneeDetails || assigneeRoleDetails || { primary: 'Unassigned' };
}

type PermissionWithAccountAndRole = Pick<
  Apollo.WorkflowPermissionSimpleFragment,
  'account' | 'role'
>;

export function getPermissionAssigneeDisplayDetails(
  permission?: PermissionWithAccountAndRole | null
) {
  return getAssigneeDisplayDetails(permission?.account, permission?.role);
}

export function getPermissionUser(permission?: PermissionWithAccountAndRole | null) {
  const account = permission?.account;
  if (account?.__typename === 'User') return account;
  return null;
}
